import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Auth/AuthContext';
import { fetchCompanies, updateCompanyEmailSettings } from '../../Utils/api';
import './myaccount.css';


const TOGGLE_FIELDS = [
  { key: 'showCheckIn',           label: 'Check-In',                    description: 'Show the time the technician arrived.' },
  { key: 'showCheckOut',          label: 'Check-Out',                   description: 'Show the time the technician finished.' },
  { key: 'showPoolCondition',     label: 'Outside & Pool Environment',  description: 'Show outdoor condition, water level, and algae status.' },
  { key: 'showChemicalLevels',    label: 'Chemical Readings',           description: 'Show pH, chlorine, alkalinity, and stabilizer readings.' },
  { key: 'showFilterPressure',    label: 'Filter Pressure',             description: 'Show filter breakdown status and pressure PSI reading.' },
  { key: 'showChemicalsAdded',    label: 'Chemical Additions',          description: 'Show which chemicals were added during the visit.' },
  { key: 'showServicesPerformed', label: 'Service Actions',             description: 'Show brushing, vacuuming, backwash, and other tasks.' },
  { key: 'showComments',          label: 'Additional Comments',         description: 'Show log and service comments left by the technician.' },
  { key: 'showEvidencePhotos',    label: 'Photo Evidence',              description: 'Show photos taken during the service visit.' },
];

const defaultSettings = {
  showCheckIn: true,
  showCheckOut: true,
  showPoolCondition: true,
  showChemicalLevels: true,
  showFilterPressure: true,
  showChemicalsAdded: true,
  showServicesPerformed: true,
  showComments: true,
  showEvidencePhotos: true,
};

const MyAccount = () => {
  const { companyId } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    if (!companyId) return;
    fetchCompanies(companyId)
      .then((companies) => {
        const co = Array.isArray(companies) ? companies[0] : companies;
        if (co) {
          setSettings({
            showCheckIn:           co.emailShowCheckIn          ?? true,
            showCheckOut:          co.emailShowCheckOut         ?? true,
            showPoolCondition:     co.emailShowPoolCondition    ?? true,
            showChemicalLevels:    co.emailShowChemicalLevels   ?? true,
            showFilterPressure:    co.emailShowFilterPressure   ?? true,
            showChemicalsAdded:    co.emailShowChemicalsAdded   ?? true,
            showServicesPerformed: co.emailShowServicesPerformed ?? true,
            showComments:          co.emailShowComments         ?? true,
            showEvidencePhotos:    co.emailShowEvidencePhotos   ?? true,
          });
        }
      })
      .catch((err) => console.error('Failed to load company:', err))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await updateCompanyEmailSettings(companyId, settings);
      setSaveStatus('success');
    } catch (err) {
      console.error('Failed to save email settings:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="ma-container"><p className="ma-loading">Loading...</p></div>;
  }

  return (
    <div className="ma-container">
      {/* Email Report Settings */}
      <div className="ma-card">
        <h2 className="ma-card-title">Customer Email Report Settings</h2>
        <p className="ma-card-desc">
          Choose which sections appear in the service completion email sent to your customers after each visit.
        </p>

        <div className="ma-toggles">
          {TOGGLE_FIELDS.map(({ key, label, description }) => (
            <div key={key} className="ma-toggle-row">
              <div className="ma-toggle-info">
                <span className="ma-toggle-label">{label}</span>
                <span className="ma-toggle-desc">{description}</span>
              </div>
              <label className="ma-switch">
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={() => handleToggle(key)}
                />
                <span className="ma-slider" />
              </label>
            </div>
          ))}
        </div>

        <div className="ma-save-row">
          {saveStatus === 'success' && (
            <span className="ma-save-msg ma-save-success">Settings saved successfully.</span>
          )}
          {saveStatus === 'error' && (
            <span className="ma-save-msg ma-save-error">Failed to save. Please try again.</span>
          )}
          <button
            className="ma-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
